import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExpiringContractItem } from '@/hooks/usePortfolioRisk';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ExpiringContractsWidgetProps {
  count: number;
  items: ExpiringContractItem[];
  loading?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function daysColor(days: number): string {
  if (days <= 7) return 'text-red-600 dark:text-red-400';
  if (days <= 14) return 'text-orange-600 dark:text-orange-400';
  return 'text-amber-600 dark:text-amber-400';
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function WidgetSkeleton() {
  return (
    <div role="status" aria-label="Loading expiring contracts data" className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-muted" aria-hidden="true" />
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Expiring Contracts widget — FR-086 Widget 3.
 * Active contracts with end_date within the next 30 days.
 * Orange accent, sorted by end_date ascending.
 */
export function ExpiringContractsWidget({ count, items, loading }: ExpiringContractsWidgetProps) {
  return (
    <section
      className="rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950 p-4 space-y-3"
      aria-label="Expiring contracts within 30 days"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-orange-600 dark:text-orange-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100">Expiring Soon</h3>
        </div>
        <span
          className="rounded-full bg-orange-100 dark:bg-orange-900 px-2 py-0.5 text-xs font-bold text-orange-700 dark:text-orange-300"
          aria-live="polite"
          aria-label={`${count} contracts expiring within 30 days`}
        >
          {count}
        </span>
      </div>

      {loading ? (
        <WidgetSkeleton />
      ) : items.length === 0 ? (
        <p className="text-xs text-orange-700 dark:text-orange-300 italic">No contracts expiring soon.</p>
      ) : (
        <ul className="space-y-1.5" role="list" aria-label="Contracts expiring within 30 days">
          {items.slice(0, 5).map((item) => (
            <li key={item.contract_id} className="flex items-center justify-between gap-2">
              <Link
                to={`/contracts/${item.contract_id}`}
                className={cn(
                  'truncate text-xs text-orange-800 dark:text-orange-200 underline hover:no-underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                )}
                title={`${item.title}${item.counterparty ? ` — ${item.counterparty}` : ''}`}
              >
                {item.title}
              </Link>
              <span className={cn('shrink-0 text-xs font-medium', daysColor(item.days_remaining))}>
                {item.days_remaining}d
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/contracts?expiring=30"
        className="block text-xs font-medium text-orange-700 dark:text-orange-300 underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        View all expiring contracts
      </Link>
    </section>
  );
}
